/**
 * TravelTodoPanel — right-drawer showing the trip's todos.
 *
 * Currently focuses on the "packing" category (that's the seeded category
 * Feature 6 delivers), but the panel is generic — a future "visa paperwork"
 * tab or a "grocery list for day 1" button could share it. The `category`
 * prop determines which slice shows.
 *
 * The "Generate packing list" button kicks the AI generator. Results are
 * reported via a toast with the count of new items plus a hint at how many
 * duplicates were skipped so users don't wonder why nothing appeared when
 * they re-run it on the same trip.
 */

import { useMemo, useState } from 'react';
import { Sparkles, Plus, Trash2, Loader2, Luggage, RefreshCw } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui/use-toast';
import { useTravelTodos, useGeneratePackingList, useTravelFeature } from '../hooks';

interface Props {
  tripId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Defaults to 'packing' so the panel drops in as the packing list. */
  category?: string;
  /** Title shown in the drawer header. Defaults to "Packing list". */
  title?: string;
}

export function TravelTodoPanel({
  tripId,
  open,
  onOpenChange,
  category = 'packing',
  title = 'Packing list',
}: Props) {
  const packingFeature = useTravelFeature('packing_list');
  const { todos, loading, createTodo, toggleTodo, deleteTodo, isCreating } = useTravelTodos(
    tripId,
    category,
  );
  const { generate, isGenerating, featureEnabled } = useGeneratePackingList(tripId);
  const [newTitle, setNewTitle] = useState('');
  const [sortBy, setSortBy] = useState<'added' | 'completed'>('added');
  const [confirmRegenOpen, setConfirmRegenOpen] = useState(false);

  const sortedTodos = useMemo(() => {
    const copy = [...todos];
    if (sortBy === 'completed') {
      // Unchecked first, checked sink to the bottom; insertion order within each.
      return copy.sort(
        (a, b) =>
          Number(a.completed) - Number(b.completed) ||
          a.sortOrder - b.sortOrder ||
          (a.createdAt < b.createdAt ? -1 : 1),
      );
    }
    // Date added — oldest first, in the order items were put on the list.
    return copy.sort((a, b) =>
      a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.sortOrder - b.sortOrder,
    );
  }, [todos, sortBy]);

  const handleAdd = async () => {
    const t = newTitle.trim();
    if (!t) return;
    await createTodo({ title: t, category });
    setNewTitle('');
  };

  const handleGenerate = async (replace: boolean) => {
    try {
      const res = await generate({ replace });
      const addedCount = res.inserted.length;
      const skipped = res.skippedCount;
      toast({
        title: replace
          ? `Regenerated — ${addedCount} item${addedCount === 1 ? '' : 's'}`
          : addedCount > 0
            ? `Added ${addedCount} packing item${addedCount === 1 ? '' : 's'}`
            : 'No new packing items',
        description:
          skipped > 0
            ? `${skipped} item${skipped === 1 ? '' : 's'} you added yourself were kept.`
            : undefined,
      });
    } catch (err) {
      toast({
        title: 'Could not generate packing list',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const hasItems = todos.length > 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col gap-0">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Luggage className="h-5 w-5" /> {title}
          </SheetTitle>
          <SheetDescription>
            Trip-scoped checklist. Items you tick off stay on the list (greyed out) so you can
            un-check if you misplace something en route.
          </SheetDescription>
        </SheetHeader>

        <SheetBody>
          {!packingFeature.enabled ? (
            <div className="mt-4 text-sm text-muted-foreground">
              The packing list feature is disabled. Re-enable it from Settings → Packing.
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {featureEnabled &&
                (hasItems ? (
                  <Button
                    onClick={() => setConfirmRegenOpen(true)}
                    disabled={isGenerating}
                    className="w-full"
                    variant="outline"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Regenerating…
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" /> Regenerate packing list
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={() => void handleGenerate(false)}
                    disabled={isGenerating}
                    className="w-full"
                    variant="secondary"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" /> Generate packing list
                      </>
                    )}
                  </Button>
                ))}

              <AlertDialog open={confirmRegenOpen} onOpenChange={setConfirmRegenOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Regenerate packing list?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This replaces the AI-suggested items with a fresh set — any you&apos;d checked
                      off will reset. Items you added yourself are kept as-is. This can&apos;t be
                      undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => void handleGenerate(true)}>
                      Regenerate
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <div className="flex gap-2">
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Add an item…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleAdd();
                  }}
                />
                <Button
                  size="sm"
                  onClick={() => void handleAdd()}
                  disabled={!newTitle.trim() || isCreating}
                >
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>

              {loading ? (
                <div className="space-y-1.5">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : todos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  No items yet. Use the generator or add your own above.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-1 text-xs">
                    <span className="text-muted-foreground mr-1">Sort:</span>
                    <Button
                      size="sm"
                      variant={sortBy === 'added' ? 'secondary' : 'ghost'}
                      className="h-7 px-2"
                      onClick={() => setSortBy('added')}
                      aria-pressed={sortBy === 'added'}
                    >
                      Date added
                    </Button>
                    <Button
                      size="sm"
                      variant={sortBy === 'completed' ? 'secondary' : 'ghost'}
                      className="h-7 px-2"
                      onClick={() => setSortBy('completed')}
                      aria-pressed={sortBy === 'completed'}
                    >
                      Incomplete
                    </Button>
                  </div>
                  <ul className="space-y-1">
                    {sortedTodos.map((t) => (
                      <li key={t.todoId} className="flex items-center gap-2 py-1 group">
                        <Checkbox
                          checked={t.completed}
                          onCheckedChange={(next) => void toggleTodo(t.todoId, next === true)}
                          aria-label={t.completed ? 'Mark as incomplete' : 'Mark as complete'}
                        />
                        <div className="flex-1 min-w-0">
                          <div
                            className={`text-sm truncate ${
                              t.completed ? 'line-through text-muted-foreground' : ''
                            }`}
                          >
                            {t.title}
                          </div>
                          {t.notes && (
                            <div className="text-[11px] text-muted-foreground truncate">
                              {t.notes}
                            </div>
                          )}
                        </div>
                        {t.source === 'ai' && (
                          <span className="text-[9px] uppercase tracking-wide text-muted-foreground">
                            AI
                          </span>
                        )}
                        <button
                          onClick={() => void deleteTodo(t.todoId)}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                          aria-label="Delete todo"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
